/**
 * The attachments a message was sent with, shown in the transcript.
 *
 * Until this existed, attachments were **write-only**: the composer staged them,
 * `/ask` carried their URLs, and the moment the message was sent they vanished.
 * A user who attached a drawing and asked "what is wrong with this?" got a reply
 * about a file the transcript no longer showed, and after a reload there was no
 * record it had ever been attached — which also makes a thread unreadable to
 * anyone reviewing it later. `bimgo-ai-web` keeps them on the message and draws
 * one per attachment (`Message.tsx`); this is that, in 2.5's own vocabulary.
 *
 * **An image renders as the image.** That is the whole point of attaching one —
 * a row reading `plan.png` tells the reader nothing they could not have guessed,
 * and the reply above it is usually *about* what the picture shows. The URL this
 * uses is `fileUrl`, the durable object URL, **not** `uploadUrl` — only the
 * latter carries the presigned signature that expires, which is why they are two
 * fields in the presign response. `csp.test.mjs` already requires the bucket in
 * `img-src` for exactly this, and names the failure it prevents: "an upload that
 * succeeds and an image that never appears."
 *
 * Anything that is not an image has nothing to show, so it keeps the composer's
 * chip — the same `.attach-chip` rather than a second style for the same object,
 * because a staged attachment and a sent one are one thing at two moments.
 *
 * What is dropped from the composer's version is everything implying the
 * attachment is still editable: no remove button, no upload spinner, no failure
 * state. A sent attachment has no lifecycle left. What is added is the link —
 * once it is history, opening it is the only thing left to do with it.
 */
import { useState } from "react";
import { useLocale } from "../../i18n/LocaleProvider";
import type { MessageAttachment } from "../../lib/attachments";

/** What the square badge reads for an attachment with nothing to preview. */
const KIND_BADGE: Record<MessageAttachment["kind"], string> = {
  image: "IMG",
  pdf: "PDF",
  excel: "XLS",
  dwg: "DWG",
};

function SentAttachment({ attachment }: { attachment: MessageAttachment }) {
  const { t } = useLocale();
  // An object the browser cannot load — deleted, or a bucket that does not serve
  // it — would otherwise leave a broken-image glyph where the picture was. The
  // chip still names the file and still links to it, which is strictly more than
  // the broken glyph says.
  const [unreadable, setUnreadable] = useState(false);
  const name = attachment.filename || t("messageInput.attachment");
  const title = attachment.filename || attachment.url;

  if (attachment.kind === "image" && !unreadable) {
    return (
      <li className="attach-chip is-image">
        <a href={attachment.url} target="_blank" rel="noreferrer noopener" title={title}>
          <img
            className="attach-preview"
            src={attachment.url}
            // The filename is the only description available; an empty `alt`
            // would hide the attachment from a screen reader entirely.
            alt={name}
            onError={() => setUnreadable(true)}
          />
        </a>
      </li>
    );
  }

  return (
    <li className="attach-chip">
      <span className="attach-kind" aria-hidden="true">
        {KIND_BADGE[attachment.kind]}
      </span>
      {attachment.url ? (
        <a
          className="attach-name"
          href={attachment.url}
          target="_blank"
          // `noopener` is the one that matters (the opened tab must not reach
          // `window.opener`); `noreferrer` keeps the URL out of the Referer header.
          rel="noreferrer noopener"
          // The chip truncates at 15rem, so the full name has to be reachable some
          // other way — a CJK or timestamped filename is routinely longer.
          title={title}
        >
          {name}
        </a>
      ) : (
        <span className="attach-name" title={title}>
          {name}
        </span>
      )}
    </li>
  );
}

export function MessageAttachments({ attachments }: { attachments: MessageAttachment[] | undefined }) {
  if (!attachments || attachments.length === 0) return null;

  return (
    <ul className="attach-list is-sent">
      {attachments.map((attachment, i) => (
        // The URL is the identity, but the same file attached twice is two
        // entries — so the index rides along, as it does for repeated tool rows.
        <SentAttachment key={`${attachment.url}-${i}`} attachment={attachment} />
      ))}
    </ul>
  );
}
